import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '@pixlide/ui';

/**
 * Saisie d'un code de confirmation, une case par chiffre.
 *
 * Découper le code en cases n'est pas seulement décoratif : cela indique sa longueur sans
 * texte d'aide, et supprime le doute sur les espaces. En contrepartie, ce type de champ est
 * un piège d'accessibilité classique, d'où les garanties tenues ici :
 *  - chaque case a son propre libellé (« chiffre 2 sur 4 ») ;
 *  - le COLLER remplit toutes les cases d'un coup, geste naturel quand on copie le code
 *    depuis l'email — un champ qui n'accepte qu'une frappe manuelle fait recopier à la main ;
 *  - la touche Retour arrière sur une case vide revient à la précédente, sans quoi corriger
 *    une faute demande de cliquer ;
 *  - `inputMode="numeric"` fait apparaître le pavé numérique sur mobile.
 */
export function CodeInput({
  value,
  onChange,
  length,
  disabled = false,
  invalid = false,
}: {
  value: string;
  onChange: (value: string) => void;
  length: number;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  function focusAt(index: number) {
    inputs.current[Math.min(Math.max(index, 0), length - 1)]?.focus();
  }

  function writeAt(index: number, digits: string) {
    // La valeur est reconstruite en entier plutôt que modifiée en place : c'est ce qui
    // permet à un collage de remplir plusieurs cases à partir de n'importe laquelle.
    const characters = value.padEnd(length, ' ').split('');

    for (let offset = 0; offset < digits.length && index + offset < length; offset++) {
      characters[index + offset] = digits[offset]!;
    }

    onChange(characters.join('').replace(/ /g, '').slice(0, length));

    return Math.min(index + digits.length, length - 1);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>, index: number) {
    if (event.key === 'Backspace' && value[index] === undefined) {
      event.preventDefault();
      onChange(value.slice(0, Math.max(index - 1, 0)));
      focusAt(index - 1);

      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusAt(index - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusAt(index + 1);
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>, index: number) {
    const digits = event.clipboardData.getData('text').replace(/\D/g, '');

    if (digits.length === 0) {
      return;
    }

    event.preventDefault();
    focusAt(writeAt(index, digits));
  }

  return (
    <div className="flex justify-center gap-3">
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            inputs.current[index] = element;
          }}
          value={value[index] ?? ''}
          onChange={(event) => {
            const digits = event.target.value.replace(/\D/g, '');

            if (digits.length > 0) {
              focusAt(writeAt(index, digits));
            }
          }}
          onKeyDown={(event) => handleKeyDown(event, index)}
          onPaste={(event) => handlePaste(event, index)}
          onFocus={(event) => event.target.select()}
          disabled={disabled}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          // `maxLength` volontairement supérieur à 1 : sans cela le navigateur tronque un
          // collage avant que le gestionnaire ne puisse le répartir sur les cases.
          maxLength={length}
          aria-label={`Chiffre ${index + 1} sur ${length} du code de confirmation`}
          aria-invalid={invalid}
          className={cn(
            'size-16 rounded-pill bg-white text-center text-2xl font-medium text-ink-900 ring-1 transition',
            'focus-visible:ring-2 focus-visible:outline-none disabled:opacity-60',
            invalid ? 'ring-danger-500' : 'ring-ink-100 focus-visible:ring-ink-900',
          )}
        />
      ))}
    </div>
  );
}
